import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, Select, TextField } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { bulkContacts, updateContact, useContactReferrers } from './api';
import { useKnownUsers, type KnownUser } from './users';
import type { BulkResult, ContactRecord } from './types';
import styles from './dialogs.module.css';

export type AssignKind = 'owner' | 'referrer';

export interface AssignDialogProps {
  open: boolean;
  kind: AssignKind;
  /** One contact (Kontaktakte I.07) or several (Kontakte 02 „Besitzer ändern"). */
  subjects: { id: string; name: string; owner: KnownUser | null }[];
  /** The current value of a single subject (preselected). */
  currentId?: string | null | undefined;
  onClose: () => void;
  onDone: (result: { single: ContactRecord | null; handedOver: boolean; bulk: BulkResult | null }) => void;
}

const TEXTS: Record<AssignKind, { eyebrow: string; label: string; none: string; save: string }> = {
  owner: { eyebrow: 'Besitzer ändern', label: 'Besitzer', none: '— Dialer-Pool', save: 'Speichern' },
  referrer: { eyebrow: 'Tippgeber ändern', label: 'Tippgeber', none: 'Keiner', save: 'Speichern' },
};

/**
 * Owner / referrer change with the mandatory reason (Kontakte 02 bulk action, Kontaktakte I.07 edit
 * mode): the same dialog frame as Kontakte 06 with a person select, the reason and the note that every
 * change becomes a system line in the timeline.
 */
export function AssignDialog({ open, kind, subjects, currentId, onClose, onDone }: AssignDialogProps) {
  const seen = useMemo(() => subjects.flatMap((s) => (s.owner ? [s.owner] : [])), [subjects]);
  const users = useKnownUsers(seen);
  const referrers = useContactReferrers(open && kind === 'referrer');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ value?: string | undefined; reason?: string | undefined }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const text = TEXTS[kind];
  const single = subjects.length === 1 ? subjects[0] : null;

  useEffect(() => {
    if (!open) return;
    setValue(currentId ?? '');
    setReason('');
    setErrors({});
    setBanner(null);
  }, [open, currentId]);

  const options =
    kind === 'owner'
      ? users.map((user) => ({ value: user.id, label: user.shortName }))
      : (referrers.data?.referrers ?? []).map((user) => ({ value: user.id, label: user.name }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: { value?: string | undefined; reason?: string | undefined } = {};
    if (reason.trim() === '') next.reason = 'Bitte geben Sie einen Grund an.';
    setErrors(next);
    setBanner(null);
    if (next.reason) return;
    setPending(true);
    try {
      if (single) {
        const body =
          kind === 'owner'
            ? { ownerId: value || null, reason: reason.trim() }
            : { referrerId: value || null, reason: reason.trim() };
        const result = await updateContact(single.id, body);
        onDone({ single: result.record, handedOver: result.handedOver, bulk: null });
      } else {
        const result = await bulkContacts({
          ids: subjects.map((s) => s.id),
          action: 'change_owner',
          ownerId: value || null,
          reason: reason.trim(),
        });
        onDone({ single: null, handedOver: false, bulk: result });
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fields = error.fields;
        setErrors({ value: fields['ownerId'] ?? fields['referrerId'], reason: fields['reason'] });
        if (!fields['ownerId'] && !fields['referrerId'] && !fields['reason']) setBanner(error.message);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  const formId = `assign-dialog-${kind}`;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={text.eyebrow}
      title={single ? single.name : `${subjects.length} Kontakte`}
      width={600}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            {text.save}
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <Select
          label={text.label}
          value={value}
          error={errors.value}
          placeholder={text.none}
          options={options}
          onChange={(event) => setValue(event.target.value)}
        />
        <TextField
          label="Grund"
          required
          value={reason}
          error={errors.reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </form>
    </Dialog>
  );
}
